import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Plus, Users, ClipboardList, ArrowRight, Trophy, FileText, MessageCircle, Send, Upload, X, LifeBuoy, Lightbulb, Bug, CheckCircle2, KeyRound, Clock3 } from "lucide-react";
import ParentAIPanel from "@/components/ParentAIPanel";
import { AssignmentOrganizer } from "@/components/organizer/AssignmentOrganizer";
import { ASSIGNMENT_FILE_ACCEPT, assignmentFileError, uploadAssignmentFiles } from "@/lib/assignmentFiles";
import { DashboardSkeleton, LoadError } from "@/components/PageState";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [counts, setCounts] = useState(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [newA, setNewA] = useState({ student_id: "", title: "", subject: "math", description: "", due_date: "", video_url: "", answer_key_text: "" });
  const [attachments, setAttachments] = useState([]);
  const [answerKeyFile, setAnswerKeyFile] = useState(null);
  const [nudgeStudent, setNudgeStudent] = useState("");
  const [nudgeMsg, setNudgeMsg] = useState("");
  const [helpAlerts, setHelpAlerts] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const NUDGE_PRESETS = [
    "You've got this! One mission at a time.",
    "Proud of the effort you're showing this week.",
    "Try Beach Lab today — I want to see what you find!",
    "Take a break, then come back stronger.",
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sub, ha, fb] = await Promise.all([
        api.get("/students"), api.get("/curriculum/subjects"),
        api.get("/help-alerts"), api.get("/feedback"),
      ]);
      setStudents(s.data); setSubjects(sub.data);
      setHelpAlerts(ha.data); setFeedback(fb.data); setError("");
      if (s.data[0]) setNewA((current) => current.student_id ? current : ({ ...current, student_id: s.data[0].id }));
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Parent dashboard could not load.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, retry]);

  const dismissAlert = async (id) => {
    try { await api.post(`/help-alerts/${id}/read`); setHelpAlerts((prev) => prev.filter((x) => x.id !== id)); } catch {}
  };
  const resolveFeedback = async (id) => {
    try { await api.post(`/feedback/${id}/resolve`); load(); } catch {}
  };

  const linkStudent = async (e) => {
    e.preventDefault();
    try { await api.post("/students/link", { email: linkEmail }); toast.success("Student linked"); setLinkEmail(""); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not link"); }
  };

  const createAssignment = async (e) => {
    e.preventDefault();
    if (!newA.student_id) return toast.error("Link a student first");
    try {
      const { data: assignment } = await api.post("/assignments", newA);
      try {
        if (attachments.length) await uploadAssignmentFiles(assignment.id, "attachment", attachments);
        if (answerKeyFile) await uploadAssignmentFiles(assignment.id, "answer_key", [answerKeyFile]);
        toast.success("Assignment created");
      } catch (uploadError) {
        toast.error(uploadError?.response?.data?.detail || "Assignment created, but a file upload failed");
      }
      setNewA((f) => ({ ...f, title: "", description: "", due_date: "", video_url: "", answer_key_text: "" }));
      setAttachments([]); setAnswerKeyFile(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onFilePick = async (files) => {
    const valid = [];
    for (const f of files) {
      const error = assignmentFileError(f);
      if (error) toast.error(error); else valid.push(f);
    }
    setAttachments((prev) => [...prev, ...valid].slice(0, 5));
  };

  const sendNudge = async (e) => {
    e.preventDefault();
    if (!nudgeStudent) return toast.error("Pick a student");
    if (!nudgeMsg.trim()) return toast.error("Write a message");
    try {
      await api.post("/nudges", { student_id: nudgeStudent, message: nudgeMsg });
      toast.success("Nudge sent — they'll see it on Today");
      setNudgeMsg("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <DashboardSkeleton testId="parent-dashboard-loading" cards={4} />;
  if (error) return <LoadError message={error} onRetry={()=>{setError("");setRetry((value)=>value+1);}} testId="parent-dashboard-load-error" />;

  return (
    <div className="space-y-8" data-testid="parent-dashboard">
      <div>
        <div className="chip mb-2">Parent / Teacher</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Hi, {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-600 mt-1">Assign work, track skills, and grade — with clear progress at a glance.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/worksheets/new" data-testid="new-worksheet-cta" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold transition-colors">
            <FileText size={14} /> New worksheet
          </Link>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Students" value={students.length} />
        <StatCard icon={ClipboardList} label="Active assignments" value={counts?.all ?? "–"} testId="active-assignments-count" />
        <StatCard icon={Clock3} label="Needs Grading" value={counts?.needs_grading ?? "–"} testId="needs-grading-count" />
        <StatCard icon={Trophy} label="Graded" value={counts?.graded ?? "–"} testId="graded-count" />
      </div>

      <section className="p-4 sm:p-6 rounded-3xl bg-white border border-slate-200" data-testid="dashboard-organizer-section">
        <div className="flex items-center gap-2 mb-1"><ClipboardList size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Assignments</h2></div>
        <p className="text-sm text-slate-500 mb-4">Grade, search, and tidy up. Archive keeps every record and grade; the student's view never changes.</p>
        <AssignmentOrganizer scope="dashboard" students={students} subjects={subjects} onCounts={setCounts} />
      </section>

      {/* Link student */}
      <section className="p-6 rounded-3xl bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3"><UserPlus size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Link a student</h2></div>
        <p className="text-sm text-slate-500 mb-3">Ask your student to sign up, then enter their email to link their account.</p>
        <form onSubmit={linkStudent} className="flex flex-col sm:flex-row gap-2">
          <input data-testid="link-student-email" type="email" required value={linkEmail} onChange={(e)=>setLinkEmail(e.target.value)} placeholder="student@example.com"
            className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
          <button data-testid="link-student-btn" className="px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-colors">Link student</button>
        </form>
      </section>

      {/* Nudges */}
      {students.length > 0 && (
        <section className="p-6 rounded-3xl bg-white border border-slate-200">
          <div className="flex items-center gap-2 mb-3"><MessageCircle size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Send a quick nudge</h2></div>
          <p className="text-sm text-slate-500 mb-3">A short note that shows up as a friendly banner on your student's Today screen.</p>
          <form onSubmit={sendNudge} className="grid sm:grid-cols-2 gap-3">
            <select data-testid="nudge-student" value={nudgeStudent} onChange={(e)=>setNudgeStudent(e.target.value)} required
              className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
              <option value="">Choose student…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="flex flex-wrap gap-1">
              {NUDGE_PRESETS.map((p, i) => (
                <button key={i} type="button" data-testid={`nudge-preset-${i}`} onClick={()=>setNudgeMsg(p)}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-teal-50 hover:text-teal-800 text-slate-600">
                  {p.length > 30 ? p.slice(0, 28) + "…" : p}
                </button>
              ))}
            </div>
            <textarea data-testid="nudge-message" value={nudgeMsg} onChange={(e)=>setNudgeMsg(e.target.value)} rows={2}
              placeholder="Write a short encouraging note…"
              className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200" />
            <button data-testid="nudge-send" className="sm:col-span-2 px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold inline-flex items-center justify-center gap-2">
              <Send size={14} /> Send nudge
            </button>
          </form>
        </section>
      )}

      {/* Help alerts (student used "I'm stuck") */}
      {helpAlerts.filter((h) => !h.read).length > 0 && (
        <section className="p-6 rounded-3xl bg-amber-50 border border-amber-200" data-testid="help-alerts">
          <div className="flex items-center gap-2 mb-3"><LifeBuoy size={18} className="text-amber-600" /><h2 className="font-display font-semibold text-lg">Your student asked for help</h2></div>
          <div className="grid gap-2">
            {helpAlerts.filter((h) => !h.read).map((h) => (
              <div key={h.id} data-testid={`help-alert-${h.id}`} className="p-4 rounded-2xl bg-white border border-amber-200 flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm"><b>{h.student_name}</b> needed help with <b>{h.topic || h.subject}</b> <span className="text-slate-500">({h.subject})</span></div>
                  <div className="text-sm text-slate-700 mt-1 italic">"{h.question}"</div>
                  <div className="text-xs text-slate-400 mt-1">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <button data-testid={`help-alert-dismiss-${h.id}`} onClick={() => dismissAlert(h.id)} className="text-amber-700 hover:text-amber-900 shrink-0"><X size={16} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Feedback inbox */}
      {feedback.length > 0 && (
        <section className="p-6 rounded-3xl bg-white border border-slate-200" data-testid="parent-feedback">
          <div className="flex items-center gap-2 mb-3"><MessageCircle size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">Feedback & ideas</h2></div>
          <div className="grid gap-2">
            {feedback.slice(0, 6).map((f) => (
              <div key={f.id} data-testid={`parent-feedback-${f.id}`} className={`p-4 rounded-2xl border ${f.status === "resolved" ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
                    {f.category === "issue" ? <span className="text-rose-600 flex items-center gap-1"><Bug size={12} /> Problem</span> : <span className="text-teal-700 flex items-center gap-1"><Lightbulb size={12} /> Idea</span>}
                    <span className="text-slate-400">· {f.area}</span>
                  </div>
                  {f.status !== "resolved"
                    ? <button data-testid={`parent-fb-resolve-${f.id}`} onClick={() => resolveFeedback(f.id)} className="text-xs text-slate-500 hover:text-teal-700 flex items-center gap-1"><CheckCircle2 size={14} /> Mark done</button>
                    : <span className="chip">Resolved</span>}
                </div>
                <div className="text-slate-800 mt-2 text-sm">{f.message}</div>
                <div className="text-xs text-slate-400 mt-1">{f.user_name} ({f.role})</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Students */}
      <section>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Your students</h2>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="students-grid">
          {students.map((s) => (
            <Link key={s.id} to={`/parent/student/${s.id}`} data-testid={`student-card-${s.id}`}
              className="p-6 rounded-3xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all flex items-center justify-between">
              <div>
                <div className="font-display text-lg font-semibold text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500">{s.email}</div>
                <div className="mt-3 flex gap-3 text-sm">
                  <span className="text-teal-700 font-semibold">{s.points} pts</span>
                  <span className="text-slate-500">Beach: {s.beach_location || "not set"}</span>
                </div>
              </div>
              <ArrowRight className="text-slate-400" />
            </Link>
          ))}
          {students.length === 0 && <div className="text-slate-500 text-sm">No students linked yet.</div>}
        </div>
      </section>

      {/* New assignment */}
      <section className="p-6 rounded-3xl bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3"><Plus size={18} className="text-teal-700" /><h2 className="font-display font-semibold text-lg">New assignment</h2></div>
        <form onSubmit={createAssignment} className="grid sm:grid-cols-2 gap-3">
          <select data-testid="assign-student" required value={newA.student_id} onChange={(e)=>setNewA({...newA, student_id: e.target.value})}
            className="px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none">
            <option value="">Select student…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="assign-subject" value={newA.subject} onChange={(e)=>setNewA({...newA, subject: e.target.value})}
            className="px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none">
            {subjects.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
          </select>
          <input data-testid="assign-title" required placeholder="Title (e.g., Beach Lab: Tide log)" value={newA.title} onChange={(e)=>setNewA({...newA, title: e.target.value})}
            className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
          <textarea data-testid="assign-description" placeholder="Description / instructions" value={newA.description} onChange={(e)=>setNewA({...newA, description: e.target.value})}
            className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" rows={3} />
          <input data-testid="assign-due" type="date" value={newA.due_date} onChange={(e)=>setNewA({...newA, due_date: e.target.value})}
            className="px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
          <input data-testid="assign-video" type="url" placeholder="Video URL (optional YouTube link)" value={newA.video_url} onChange={(e)=>setNewA({...newA, video_url: e.target.value})}
            className="px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
          <label className="sm:col-span-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center gap-2 cursor-pointer">
            <Upload size={16} className="text-slate-500" />
            <span className="text-sm text-slate-600">Attach files or images (10 MB each) — optional</span>
            <input data-testid="assign-files" type="file" multiple accept={ASSIGNMENT_FILE_ACCEPT} onChange={(e)=>onFilePick(Array.from(e.target.files || []))} className="hidden" />
          </label>
          {attachments.length > 0 && (
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {attachments.map((a, i) => (
                <span key={`${a.name}-${i}`} className="chip">{a.name} <button type="button" data-testid={`assign-file-remove-${i}`} aria-label={`Remove ${a.name}`} onClick={()=>setAttachments((prev)=>prev.filter((_, j) => j !== i))} className="ml-1 text-slate-500"><X size={10} /></button></span>
              ))}
            </div>
          )}
          <div className="sm:col-span-2 p-4 rounded-xl bg-amber-50 border border-amber-200 space-y-3" data-testid="assign-answer-key-section">
            <div className="flex items-center gap-2 font-semibold text-amber-900"><KeyRound size={16} /> Parent Answer Key</div>
            <p className="text-xs text-amber-800">Visible only to the linked parent account.</p>
            <textarea data-testid="assign-answer-key-text" rows={3} placeholder="Type the answer key (optional)" value={newA.answer_key_text} onChange={(e)=>setNewA({...newA, answer_key_text:e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-amber-200 bg-white" />
            <label className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-amber-300 bg-white cursor-pointer text-sm text-amber-900">
              <Upload size={16} /> {answerKeyFile ? answerKeyFile.name : "Attach parent-only answer-key file (10 MB max)"}
              <input data-testid="assign-answer-key-file" type="file" accept={ASSIGNMENT_FILE_ACCEPT} className="hidden" onChange={(e)=>{
                const file=e.target.files?.[0]; if (!file) return; const error=assignmentFileError(file); if (error) toast.error(error); else setAnswerKeyFile(file);
              }} />
            </label>
            {answerKeyFile && <button type="button" data-testid="assign-answer-key-file-remove" onClick={()=>setAnswerKeyFile(null)} className="text-xs font-semibold text-red-700">Remove selected answer-key file</button>}
          </div>
          <button data-testid="assign-submit" className="sm:col-span-2 px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-colors">Create assignment</button>
        </form>
      </section>

      {/* AI Assistant */}
      <ParentAIPanel students={students} onCreated={load} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="p-5 rounded-2xl bg-white border border-slate-200 flex items-center gap-4">
      <span className="grid place-items-center w-11 h-11 rounded-2xl bg-teal-50 text-teal-800"><Icon size={20} strokeWidth={2.4} /></span>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">{label}</div>
        <div data-testid={testId} className="font-display text-2xl font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
